import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logUpsellProductClicked from '@alltrails/amplitude/events/logUpsellProductClicked';
import useCurrencyCode from '@alltrails/context/hooks/useCurrencyCode';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import getPurchaseScreenTypePlan from '@alltrails/payment/utils/getPurchaseScreenTypePlan';
import type Coupon from '@alltrails/types/Coupon';
import type Plans from '@alltrails/types/Plans';
import type SubscriptionUser from '@alltrails/types/SubscriptionUser';
import TrialReminder from '../TrialReminder/TrialReminder';
import getPricingText from './getPricingText';
import styles from './PlanSelection.module.scss';

// Figma file: https://www.figma.com/design/PtYSSpohQ9rL7ZWCcdsYJK/2025-Upsell-System?node-id=5541-138083&t=JJQ3VDYh6Mc0P7Ol-0

type Props = {
  className?: string;
  // Renders the tier-card prices per the plans-page board: annual amount in Medium weight and no "that's" prefix on desktop.
  // Opt-in so the tiered upsell modal keeps its existing pricing format.
  compactPricing?: boolean;
  hidePeak?: boolean;
  plans: Plans;
  coupon?: Coupon;
  setTierSelected: (tier: PurchaseTier) => void;
  showPlusFirst?: boolean;
  tierSelected: PurchaseTier;
  trigger: UpsellTrigger;
  isPromoEligible: boolean;
  subscriptionUser: SubscriptionUser | null;
};

const PlanSelection = ({
  className,
  compactPricing = false,
  hidePeak = false,
  plans,
  coupon,
  setTierSelected,
  showPlusFirst = false,
  tierSelected,
  trigger,
  isPromoEligible,
  subscriptionUser
}: Props) => {
  const currencyCode = useCurrencyCode();
  const isMobileSizedScreen = useIsMobileSizedScreen();

  const plusPlanButton = (
    <button
      className={classNames(styles.planContainer, {
        [styles.plusSelected]: tierSelected === 'plus'
      })}
      onClick={() => {
        logUpsellProductClicked({
          product_type: 'plus',
          purchase_screen_type_plan: getPurchaseScreenTypePlan(plans.plus, coupon),
          trigger
        });

        setTierSelected('plus');
      }}
    >
      <div className={styles.planContentContainer}>
        <div className={styles.copy}>
          <PlusLogoBadge variant="full" />
          <Typography variant="text150">
            {getPricingText({
              plan: plans.plus,
              currencyCode,
              isPromoEligible,
              coupon: coupon?.subscriptionTier === 'plus' || coupon?.subscriptionTier === 'any' ? coupon : undefined,
              isMobileSizedScreen,
              subscriptionUser,
              compactMonthly: compactPricing,
              emphasizeAnnualCost: compactPricing
            })}
          </Typography>
        </div>
        {plans.plus.isTrial && (
          <TrialReminder cost={plans.plus.priceTotal} currencyCode={currencyCode} selected={tierSelected === 'plus'} tier="plus" variant="inline" />
        )}
      </div>
    </button>
  );

  return (
    <div className={classNames(styles.planSelectionWrapper, className)}>
      {showPlusFirst && plusPlanButton}
      {!hidePeak && (
        <button
          className={classNames(styles.planContainer, {
            [styles.peakSelected]: tierSelected === 'peak'
          })}
          onClick={() => {
            logUpsellProductClicked({
              product_type: 'peak',
              purchase_screen_type_plan: getPurchaseScreenTypePlan(plans.peak, coupon),
              trigger
            });

            setTierSelected('peak');
          }}
        >
          <div className={styles.planContentContainer}>
            <div className={styles.copy}>
              <div className={styles.peakRow}>
                <PeakLogoBadge variant="full" />
                <Typography variant="text50bold" className={styles.badge}>
                  <FormattedMessage defaultMessage="Includes Plus benefits" />
                </Typography>
              </div>
              <Typography variant="text150">
                {getPricingText({
                  plan: plans.peak,
                  currencyCode,
                  isPromoEligible,
                  coupon: coupon?.subscriptionTier === 'peak' || coupon?.subscriptionTier === 'any' ? coupon : undefined,
                  isMobileSizedScreen,
                  subscriptionUser,
                  compactMonthly: compactPricing,
                  emphasizeAnnualCost: compactPricing
                })}
              </Typography>
            </div>
            {plans.peak.isTrial && (
              <TrialReminder
                cost={plans.peak.priceTotal}
                currencyCode={currencyCode}
                selected={tierSelected === 'peak'}
                tier="peak"
                variant="inline"
              />
            )}
          </div>
        </button>
      )}
      {!showPlusFirst && plusPlanButton}
    </div>
  );
};
export default PlanSelection;
